// Client-side port of the backend uploaded-QR builder
// (backend/flask_application/apps/services/thai_qr.py: inspect_static_qr,
// build_amount_payload). The customer display rebuilds the QR live as the
// cashier edits the bill, so the output must stay byte-identical to the backend;
// tests/fixtures/thai-qr-backend-vectors.json is generated from the real
// backend (scripts/generate_thai_qr_vectors.py) and checked against this file.
//
// The uploaded payload is edited as a string (tag 01, tag 54, CRC); every other
// tag passes through untouched, so unknown issuers keep working.

import { toTwoDecimals } from './promptpay';

// The display protocol carries at most 512 characters; leave room for tag 54.
const MAX_PAYLOAD_LENGTH = 480;
const AMOUNT_TEXT_PATTERN = /^[0-9]{1,10}\.[0-9]{2}$/;

// Shop names may be written in the local language (tag 64, UTF-8, e.g. Thai), so text
// outside ASCII is allowed, but only in the tags that carry names. Control, format and
// unassigned characters are never accepted anywhere (same rule as the backend).
const NON_ASCII_ALLOWED_TAGS = ['59', '60', '64'];
const DISALLOWED_CHARACTER = /[\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}]/u;
const HEADER = /^[0-9]{4}$/; // ASCII digits only

export const DEFAULT_CONVENTION = { point_of_initiation: '12', amount_before: null };

function tlv(tag, value) {
  return tag + (value.length < 10 ? '0' : '') + value.length + value;
}

// CRC-16/CCITT-FALSE over the UTF-8 bytes, four uppercase hex digits (equals the ASCII
// version for ASCII text).
export function crc16Utf8(text) {
  const bytes = new TextEncoder().encode(text);
  let checksum = 0xffff;
  for (let i = 0; i < bytes.length; i += 1) {
    checksum ^= bytes[i] << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      checksum = checksum & 0x8000 ? ((checksum << 1) ^ 0x1021) & 0xffff : (checksum << 1) & 0xffff;
    }
  }
  return checksum.toString(16).toUpperCase().padStart(4, '0');
}

function utf8Length(character) {
  const code = character.codePointAt(0);
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  if (code < 0x10000) return 3;
  return 4;
}

// [[tag, value, raw], ...] where raw is the item exactly as written, or null when the
// text is not well-formed TLV. `unit` says how the two-digit length counts a value:
// 'bytes' = UTF-8 bytes, 'chars' = characters (the same for ASCII). Works on code points.
function parseRaw(chars, unit) {
  const items = [];
  let position = 0;
  while (position < chars.length) {
    const header = chars.slice(position, position + 4).join('');
    if (!HEADER.test(header)) return null;
    const length = parseInt(header.slice(2, 4), 10);
    let end = position + 4;
    let used = 0;
    while (used < length) {
      if (end >= chars.length) return null;
      used += unit === 'bytes' ? utf8Length(chars[end]) : 1;
      end += 1;
    }
    if (used !== length) return null; // the length ends in the middle of a character
    items.push([header.slice(0, 2), chars.slice(position + 4, end).join(''), chars.slice(position, end).join('')]);
    position = end;
  }
  return items;
}

// EMV counts UTF-8 data in bytes, but a QR that counts characters is also accepted.
function parseTlvRaw(text) {
  const chars = Array.from(text);
  return parseRaw(chars, 'bytes') || parseRaw(chars, 'chars');
}

function allowedCharacter(character) {
  const code = character.codePointAt(0);
  if (code < 128) return code >= 32 && code < 127;
  return !DISALLOWED_CHARACTER.test(character);
}

// The accepted static payload as [text, items], or null when the backend would reject it.
function acceptStaticPayload(payload) {
  if (typeof payload !== 'string') return null;
  const text = payload.trim();
  const chars = Array.from(text);
  if (!chars.length || chars.length > MAX_PAYLOAD_LENGTH || !chars.every(allowedCharacter)) return null;
  const raw = parseTlvRaw(text);
  if (!raw) return null;
  const items = raw.map(([tag, value]) => [tag, value]);
  if (items.some(([tag, value]) => (
    NON_ASCII_ALLOWED_TAGS.indexOf(tag) === -1 && Array.from(value).some((c) => c.codePointAt(0) > 126)
  ))) return null;
  const tags = items.map((item) => item[0]);
  if (new Set(tags).size !== tags.length) return null;
  const last = items[items.length - 1];
  if (!last || last[0] !== '63' || Array.from(last[1]).length !== 4) return null;
  if (crc16Utf8(chars.slice(0, -4).join('')) !== chars.slice(-4).join('').toUpperCase()) return null;
  const root = {};
  items.forEach(([tag, value]) => { root[tag] = value; });
  if (root['00'] !== '01' || !('01' in root) || '54' in root) return null;
  if ('53' in root && root['53'] !== '764') return null;
  return [text, raw];
}

export function normalizeConvention(convention) {
  if (!convention || typeof convention !== 'object' || Array.isArray(convention)) {
    return { ...DEFAULT_CONVENTION };
  }
  const poi = convention.point_of_initiation === '11' || convention.point_of_initiation === '12'
    ? convention.point_of_initiation
    : DEFAULT_CONVENTION.point_of_initiation;
  const before = typeof convention.amount_before === 'string' && /^[0-9]{2}$/.test(convention.amount_before)
    ? convention.amount_before
    : null;
  return { point_of_initiation: poi, amount_before: before };
}

// Returns '' whenever the backend raises (unusable payload or amount).
export function buildTemplatePayload(staticPayload, amount, convention) {
  const accepted = acceptStaticPayload(staticPayload);
  if (!accepted) return '';
  if (amount === null || amount === undefined || typeof amount === 'boolean') return '';
  const amountText = toTwoDecimals(amount);
  if (amountText === null || !AMOUNT_TEXT_PATTERN.test(amountText)) return '';

  const rule = normalizeConvention(convention);
  // Items are kept exactly as written (raw); lengths are never recomputed, which matters
  // for UTF-8 names whose length may count bytes or characters.
  const items = accepted[1]
    .filter(([tag]) => tag !== '63')
    .map(([tag, , raw]) => [tag, tag === '01' ? tlv('01', rule.point_of_initiation) : raw]);

  let insertAt = items.length;
  if (rule.amount_before === '63') {
    // last tag before the CRC
  } else if (rule.amount_before && items.some(([tag]) => tag === rule.amount_before)) {
    insertAt = items.findIndex(([tag]) => tag === rule.amount_before);
  } else {
    // Ascending tag order: in front of the first tag numbered above 54.
    const index = items.findIndex(([tag]) => tag > '54');
    if (index !== -1) insertAt = index;
  }
  items.splice(insertAt, 0, ['54', tlv('54', amountText)]);

  const body = items.map((item) => item[1]).join('') + '6304';
  return body + crc16Utf8(body);
}
